import { Injectable } from '@angular/core';
import { HttpRestService } from '@core/services/http-rest.service';
import { APIResponse } from '@yourcause/common';
import { PersonaTypings } from '@core/typings/persona.typing';
import { catchError, map, Observable, throwError } from 'rxjs';
import { NotifierService } from '@yourcause/common/notifier';
import { I18nService } from '@yourcause/common/i18n';

@Injectable({ providedIn: 'root' })
export class PersonaResources {

  constructor (
    private httpRestService: HttpRestService,
    private notifierService: NotifierService,
    private i18n: I18nService
  ) { }

  /**
   * get questions and answers
   * @returns questions and answers as observable from API
   */
  getPersonaQuestionsAndAnswers (): Observable<PersonaTypings.GetPersonaQuestionsAndAnswersQueryDto> {
    const endpoint = `api/Persona`;

    return this.httpRestService.Get<APIResponse<PersonaTypings.GetPersonaQuestionsAndAnswersQueryDto>>(endpoint)
      .pipe(
        map(e => e.data),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * get answers, user level
   * @returns users answers at user level as observable from API
   */
  getPersonaUserAnswers (): Observable<PersonaTypings.GetPersonaUserAnswersQueryDto> {
    const endpoint = `api/Persona/User/Answers`;

    return this.httpRestService.Get<APIResponse<PersonaTypings.GetPersonaUserAnswersQueryDto>>(endpoint)
      .pipe(
        map(e => e.data),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * get answers, nonprofit level
   * @returns users answers at nonprofit level as observable from API
   */
  getPersonaUserNonprofitAnswers (nonprofitId: number): Observable<PersonaTypings.GetPersonaUserNonprofitAnswersQueryDto> {
    const endpoint = `api/Persona/User/Nonprofit/${nonprofitId}/Answers`;

    return this.httpRestService.Get<APIResponse<PersonaTypings.GetPersonaUserNonprofitAnswersQueryDto>>(endpoint)
      .pipe(
        map(e => e.data),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * get answers, staged nonprofit level
   * @returns users answers at staged nonprofit level as observable from API
   */
  getPersonaUserStagedNonprofitAnswers (stagedNonprofitId: number): Observable<PersonaTypings.GetPersonaUserStagedNonprofitAnswersQueryDto> {
    const endpoint = `api/Persona/User/StagedNonprofit/${stagedNonprofitId}/Answers`;

    return this.httpRestService.Get<APIResponse<PersonaTypings.GetPersonaUserStagedNonprofitAnswersQueryDto>>(endpoint)
      .pipe(
        map(e => e.data),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * get answers, nonprofit level, for review
   * @returns users answers at nonprofit level as observable from API for reviewer
   */
  getPersonaUserNonprofitAnswersForReview (userId: number, nonprofitId: number, nonprofitAdminRequestId: number): Observable<PersonaTypings.GetPersonaUserNonprofitAnswersForReviewQueryDto> {
    const endpoint = `api/Persona/User/${userId}/Nonprofit/${nonprofitId}/Answers/ForReview/${nonprofitAdminRequestId}`;

    return this.httpRestService.Get<APIResponse<PersonaTypings.GetPersonaUserNonprofitAnswersForReviewQueryDto>>(endpoint)
      .pipe(
        map(e => e.data),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * get answers, staged nonprofit level, for review
   * @returns users answers at staged nonprofit level as observable from API for reviewer
   */
  getPersonaUserStagedNonprofitAnswersForReview (userId: number, stagedNonprofitId: number, nonprofitAdminRequestId: number): Observable<PersonaTypings.GetPersonaUserStagedNonprofitAnswersForReviewQueryDto> {
    const endpoint = `api/Persona/User/${userId}/StagedNonprofit/${stagedNonprofitId}/Answers/ForReview/${nonprofitAdminRequestId}`;

    return this.httpRestService.Get<APIResponse<PersonaTypings.GetPersonaUserStagedNonprofitAnswersForReviewQueryDto>>(endpoint)
      .pipe(
        map(e => e.data),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Creates or updates a user answer to a persona question.
   * @param systemAnswerId identity of the specific system answer
   * @param personaQuestionAndAnswerTypeWithDetails context (user, nonprofit, staged nonprofit)
   * @param customResponse optional custom response text provided by the user
   * @param userSystemAnswerId when provided, the existing record with this id is updated; omit to create a new answer
   * @returns the upserted answer id and the system answer id
   */
  upsertPersonaUserAnswer (
    systemAnswerId: number,
    personaQuestionAndAnswerTypeWithDetails: PersonaTypings.PersonaQuestionAndAnswerTypeWithDetails,
    customResponse?: string,
    userSystemAnswerId?: number
  ): Observable<{ userSystemAnswerId: PersonaTypings.NewPersonaAnswer; systemAnswerId: number; }> {
    const personaQuestionAndAnswerType = personaQuestionAndAnswerTypeWithDetails.personaQuestionAndAnswerType;
    const nonprofitId = personaQuestionAndAnswerTypeWithDetails.nonprofitId;
    const stagedNonprofitId = personaQuestionAndAnswerTypeWithDetails.stagedNonprofitId;
    const userId = personaQuestionAndAnswerTypeWithDetails.userId;
    const nonprofitAdminRequestId = personaQuestionAndAnswerTypeWithDetails.nonprofitAdminRequestId;
    const beingReviewed = personaQuestionAndAnswerTypeWithDetails.beingReviewed;

    let endpoint = null;

    const request = {
      customResponse,
      systemAnswerId,
      nonprofitId,
      stagedNonprofitId,
      personaUserAnswerId: userSystemAnswerId ?? null,
      personaUserNonprofitAnswerId: userSystemAnswerId ?? null,
      personaUserStagedNonprofitAnswerId: userSystemAnswerId ?? null
    };

    switch (personaQuestionAndAnswerType) {
      case PersonaTypings.PersonaQuestionAndAnswerTypes.User:
        endpoint = `api/Persona/User/Answer`;

        break;
      case PersonaTypings.PersonaQuestionAndAnswerTypes.Nonprofit:
        if (beingReviewed) {
          endpoint = `api/Persona/User/${userId}/Nonprofit/${nonprofitId}/Answers/ForReview/${nonprofitAdminRequestId}`;
        } else {
          endpoint = `api/Persona/User/Nonprofit/Answer`;
        }

        break;
      case PersonaTypings.PersonaQuestionAndAnswerTypes.StagedNonprofit:
        if (beingReviewed) {
          endpoint = `api/Persona/User/${userId}/StagedNonprofit/${stagedNonprofitId}/Answers/ForReview/${nonprofitAdminRequestId}`;
        } else {
          endpoint = `api/Persona/User/StagedNonprofit/Answer`;
        }

        break;
    }

    if (!endpoint) {
      throw ("Provided PersonaQuestionAndAnswerType is not valid.");
    }

    return this.httpRestService.Post<APIResponse<PersonaTypings.NewPersonaAnswer>>(endpoint, request)
      .pipe(
        map(e => {
          return { userSystemAnswerId: e.data, systemAnswerId: systemAnswerId };
        }),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * deletes the provided answer
   * @param userSystemAnswerId
   * @param systemAnswerId
   * @param personaQuestionAndAnswerTypeWithDetails
   * @returns
   */
  deletePersonaUserAnswer (
    userSystemAnswerId: number,
    systemAnswerId: number,
    personaQuestionAndAnswerTypeWithDetails: PersonaTypings.PersonaQuestionAndAnswerTypeWithDetails
  ) {
    const personaQuestionAndAnswerType = personaQuestionAndAnswerTypeWithDetails.personaQuestionAndAnswerType;
    const nonprofitId = personaQuestionAndAnswerTypeWithDetails.nonprofitId;
    const stagedNonprofitId = personaQuestionAndAnswerTypeWithDetails.stagedNonprofitId;
    const userId = personaQuestionAndAnswerTypeWithDetails.userId;
    const nonprofitAdminRequestId = personaQuestionAndAnswerTypeWithDetails.nonprofitAdminRequestId;
    const beingReviewed = personaQuestionAndAnswerTypeWithDetails.beingReviewed;
    let endpoint = null;

    const request = {
      systemAnswerId,
      nonprofitId,
      stagedNonprofitId,
      personaUserAnswerId: userSystemAnswerId,
      personaUserNonprofitAnswerId: userSystemAnswerId,
      personaUserStagedNonprofitAnswerId: userSystemAnswerId
    };

    switch (personaQuestionAndAnswerType) {
      case PersonaTypings.PersonaQuestionAndAnswerTypes.User:
        endpoint = `api/Persona/User/Answer`;

        break;
      case PersonaTypings.PersonaQuestionAndAnswerTypes.Nonprofit:
        if (beingReviewed) {
          endpoint = `api/Persona/User/${userId}/Nonprofit/${nonprofitId}/Answers/ForReview/${nonprofitAdminRequestId}`;
        } else {
          endpoint = `api/Persona/User/Nonprofit/Answer`;
        }

        break;
      case PersonaTypings.PersonaQuestionAndAnswerTypes.StagedNonprofit:
        if (beingReviewed) {
          endpoint = `api/Persona/User/${userId}/StagedNonprofit/${stagedNonprofitId}/Answers/ForReview/${nonprofitAdminRequestId}`;
        } else {
          endpoint = `api/Persona/User/StagedNonprofit/Answer`;
        }

        break;
    }

    if (!endpoint) {
      throw ("Provided PersonaQuestionAndAnswerType is not valid.");
    }

    return this.httpRestService.Delete(endpoint, request)
      .pipe(
        map(_ => true),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Display an error based on status code to the user.
   *
   * @param error http error thrown
   * @returns throws the error so that the caller can handle it
   */
  private handleError (error: any): Observable<never> {
    switch (error.status) {
      case 404:
        this.notifierService.error(error.error.detail);
        break;
      case 400:
        this.notifierService.error(error.error.detail);
        break;
      default:
        this.notifierService.error(this.i18n.translate(
          'error:textGeneric',
          {},
          'An error has occurred. Please try again later.'
        ));
    }

    return throwError(() => error);
  }
}
